import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { appReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

// Add these when you have examples ready
// const image1 = `${process.env.PUBLIC_URL}/assets/appExample1.png`;
// const image2 = `${process.env.PUBLIC_URL}/assets/appExample2.png`;
// const image3 = `${process.env.PUBLIC_URL}/assets/appExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;

    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function CustomApps() {
  const safeReviews = Array.isArray(appReviews) ? appReviews : [];
  const title = "Custom Apps & Tools — Lightweight Internal Tools, Portals, and Workflow Apps";
  const description =
    "Custom apps and tools built around your workflow. Internal dashboards, admin tools, portals, import/export utilities, and lightweight business apps.";
  const canonical = "https://brainwavesolutions.net/services/custom-apps";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Custom Apps & Tools", url: canonical },
    ]),
    buildServiceSchema({
      name: "Custom Apps & Tools",
      description,
      canonical,
      offerLabel: "$300–$7,500+",
      serviceType: "Custom apps and internal tools",
    }),
  ];

  const images = [
    // {
    //   image: image1,
    //   altText:
    //     "Custom internal business app showing dashboard panels, navigation, and workflow tools.",
    // },
    // {
    //   image: image2,
    //   altText:
    //     "Lightweight admin tool interface with data table, filters, and action controls for business operations.",
    // },
    // {
    //   image: image3,
    //   altText:
    //     "Custom web app example showing clean layout, task workflow, and data management tools.",
    // },
  ].filter((img) => img.image);

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Custom apps and tools built around the way you actually work
              </Typography>

              <p className="bws-gsl-lead">
                Sometimes a spreadsheet is no longer enough, but a giant off-the-shelf system is more than you need.
                In between those two extremes, there is often room for a simple internal tool that saves time, reduces
                confusion, and fits your workflow much better than forcing your process into the wrong software.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses build lightweight apps, internal dashboards, admin tools, and utilities that solve
                practical problems without overcomplicating the solution. The goal is not software for its own sake —
                it is creating something that makes the work easier, clearer, and more reliable.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Internal tools</span>
                <span className="bws-gsl-badge">Mini dashboards</span>
                <span className="bws-gsl-badge">Admin panels</span>
                <span className="bws-gsl-badge">Workflow apps</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Talk about your app idea
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common app/tool problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Processes that outgrew spreadsheets</li>
                  <li>Teams juggling too many manual steps and different tools</li>
                  <li>Internal workflows with no place to manage data or actions</li>
                  <li>Repeated tasks that should be handled by a single tool instead</li>
                  <li>Business processes that need a portal, admin view, or utility</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message describing the workflow, what is frustrating now, and what you wish the tool could do.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Simplify the workflow</Typography>
              <Typography className="bws-gsl-stripText">
                Give your team one clearer place to handle the task instead of bouncing between tools.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Reduce manual steps</Typography>
              <Typography className="bws-gsl-stripText">
                Replace repetitive actions and scattered processes with a cleaner, more focused tool.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Build only what you need</Typography>
              <Typography className="bws-gsl-stripText">
                Create a practical solution without the overhead of a bloated system.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Examples of lightweight apps, internal tools, admin panels, and workflow utilities.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Internal utilities and workflow tools</li>
                    <li>Mini dashboards and admin panels</li>
                    <li>Simple portals and business apps</li>
                    <li>Import/export utilities and support tools</li>
                    <li>Replacing repetitive manual workflows with cleaner software</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Businesses that outgrew spreadsheets for a key process</li>
                    <li>Teams needing an internal tool, not a giant platform</li>
                    <li>Operations workflows that need a clearer interface</li>
                    <li>Anyone wanting a practical custom solution without unnecessary complexity</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Do I need a full custom app, or just a simpler tool?</Typography>
                <Typography className="bws-gsl-faqA">
                  That depends on the workflow. In many cases, the best solution is smaller and simpler than people expect.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you build something internal for my team to use?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. Internal dashboards, admin views, utilities, and focused workflow tools are often a very good fit.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the workflow, what is frustrating now, and what you want the finished tool to help people do.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need a custom tool for a workflow?</Typography>
              <p className="bws-muted">
                Send a quick description of the process and what kind of tool would make it easier.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
